/* ============================================
   AUDIO SPECTRUM COMPONENT
   The Creative Orchestra - GEMINI Framework
   Musical orchestra audio spectrum visualizer
   ============================================ */

import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAudio } from '@context/AudioContext';
import { SPECTRUM_CONFIG } from '@utils/constants';

/**
 * Audio Spectrum Visualizer Component
 * Creates an animated spectrum display that responds to audio
 * When audio is muted/paused, shows a gentle idle animation
 */
const AudioSpectrum = ({ 
  barCount = SPECTRUM_CONFIG.barCount || 32,
  className = '',
  position = 'bottom', // 'bottom' | 'center' | 'sides'
  height = 120,
  colorScheme = 'gradient', // 'gradient' | 'solid' | 'rainbow'
}) => {
  const { isPlaying, isMuted, volume } = useAudio();
  const [bars, setBars] = useState(() => 
    Array(barCount).fill(0).map(() => Math.random() * 20 + 10)
  );
  const animationRef = useRef(null);
  const lastUpdateRef = useRef(0);

  // Check if audio is active
  const isActive = isPlaying && !isMuted && volume > 0;

  // Generate bar heights based on audio state
  useEffect(() => {
    const updateBars = (timestamp) => {
      // Throttle updates to ~30fps
      if (timestamp - lastUpdateRef.current < 33) {
        animationRef.current = requestAnimationFrame(updateBars);
        return;
      }
      lastUpdateRef.current = timestamp;

      setBars(prevBars => 
        prevBars.map((_, index) => {
          if (isActive) {
            // Active audio - dynamic heights
            const baseHeight = volume * 60;
            const wave = Math.sin((index / barCount) * Math.PI * 2 + timestamp / 300);
            const wave2 = Math.cos((index / barCount) * Math.PI * 4 + timestamp / 500);
            const randomFactor = Math.random() * 25;
            return Math.max(8, Math.min(100, baseHeight + wave * 15 + wave2 * 10 + randomFactor));
          } else {
            // Idle state - gentle wave
            const idleWave = Math.sin((index / barCount) * Math.PI * 2 + timestamp / 1000);
            return 10 + idleWave * 5 + Math.random() * 3;
          }
        })
      );

      animationRef.current = requestAnimationFrame(updateBars);
    };

    animationRef.current = requestAnimationFrame(updateBars);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, volume, barCount]);

  // Get bar color based on scheme and index
  const getBarColor = (index, total) => {
    switch (colorScheme) {
      case 'rainbow':
        return `hsl(${(index / total) * 360}, 70%, 60%)`;
      case 'solid':
        return 'rgba(129, 140, 248, 0.8)';
      case 'gradient':
      default:
        const hue = 230 + (index / total) * 60; // Purple to blue range
        return `hsl(${hue}, 70%, 65%)`;
    }
  };

  // Position styles
  const positionStyles = {
    bottom: 'items-end justify-center',
    center: 'items-center justify-center',
    sides: 'items-end justify-between',
  };

  return (
    <div 
      className={`flex gap-[2px] ${positionStyles[position]} ${className}`}
      style={{ height }}
      aria-hidden="true"
    >
      {bars.map((barHeight, index) => (
        <motion.div
          key={index}
          className="rounded-full"
          style={{
            width: `${100 / barCount - 1}%`,
            maxWidth: 8,
            minWidth: 2,
            height: `${barHeight}%`,
            background: getBarColor(index, barCount),
            boxShadow: isActive 
              ? `0 0 10px ${getBarColor(index, barCount)}40`
              : 'none',
          }}
          initial={false}
          animate={{ height: `${barHeight}%` }}
          transition={{
            duration: 0.05,
            ease: 'linear',
          }}
        />
      ))}
    </div>
  );
};

/**
 * Circular Audio Spectrum
 * Displays spectrum in a circular/radial pattern
 */
export const CircularSpectrum = ({
  barCount = 64,
  radius = 150,
  className = '',
}) => {
  const { isPlaying, isMuted, volume } = useAudio();
  const [bars, setBars] = useState(() => 
    Array(barCount).fill(0).map(() => Math.random() * 20 + 10)
  );
  const animationRef = useRef(null);

  const isActive = isPlaying && !isMuted && volume > 0;

  useEffect(() => {
    const updateBars = (timestamp) => {
      setBars(prevBars => 
        prevBars.map((_, index) => {
          if (isActive) {
            const wave = Math.sin((index / barCount) * Math.PI * 4 + timestamp / 200);
            return Math.max(10, Math.min(50, volume * 30 + wave * 15 + Math.random() * 10));
          } else {
            return 10 + Math.sin((index / barCount) * Math.PI * 2 + timestamp / 1000) * 3;
          }
        })
      );
      animationRef.current = requestAnimationFrame(updateBars);
    };

    animationRef.current = requestAnimationFrame(updateBars);
    return () => cancelAnimationFrame(animationRef.current);
  }, [isActive, volume, barCount]);

  return (
    <div 
      className={`relative ${className}`}
      style={{ width: radius * 2, height: radius * 2 }}
    >
      <svg viewBox={`0 0 ${radius * 2} ${radius * 2}`} className="w-full h-full">
        <defs>
          <filter id="spectrumGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        {bars.map((barHeight, index) => {
          const angle = (index / barCount) * Math.PI * 2 - Math.PI / 2;
          const innerRadius = radius * 0.6;
          const outerRadius = innerRadius + barHeight;
          
          const x1 = radius + Math.cos(angle) * innerRadius;
          const y1 = radius + Math.sin(angle) * innerRadius;
          const x2 = radius + Math.cos(angle) * outerRadius;
          const y2 = radius + Math.sin(angle) * outerRadius;

          const hue = 230 + (index / barCount) * 60;

          return (
            <line
              key={index}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={`hsl(${hue}, 70%, 65%)`}
              strokeWidth={2}
              strokeLinecap="round"
              filter={isActive ? 'url(#spectrumGlow)' : undefined}
              opacity={isActive ? 0.9 : 0.4}
            />
          );
        })}
      </svg>
    </div>
  );
};

/**
 * Mini Spectrum Indicator
 * Small spectrum for use in UI elements like buttons
 */
export const MiniSpectrum = ({ 
  barCount = 5,
  className = '',
  size = 'md',
}) => {
  const { isPlaying, isMuted, volume } = useAudio();
  const [bars, setBars] = useState(() => Array(barCount).fill(30));

  const isActive = isPlaying && !isMuted;

  const sizes = {
    sm: { height: 12, width: 16 },
    md: { height: 16, width: 24 },
    lg: { height: 20, width: 32 },
  };

  const { height, width } = sizes[size] || sizes.md;

  useEffect(() => {
    if (!isActive) {
      setBars(Array(barCount).fill(30));
      return;
    }

    const interval = setInterval(() => {
      setBars(prevBars => 
        prevBars.map(() => Math.random() * 70 + 30)
      );
    }, 100);

    return () => clearInterval(interval);
  }, [isActive, barCount]);

  return (
    <div 
      className={`flex items-end gap-px ${className}`}
      style={{ height, width }}
    >
      {bars.map((barHeight, index) => (
        <div
          key={index}
          className="flex-1 bg-current rounded-full transition-all duration-100"
          style={{ height: `${barHeight}%` }}
        />
      ))}
    </div>
  );
};

export default AudioSpectrum;
